import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EnvelopeSimple } from "@phosphor-icons/react";

export default function SmtpSettingsPanel({ endpoint = "/tenant/smtp-settings", title = "E-Mail-Versand (eigener SMTP)", description }) {
  const [f, setF] = useState({ host: "", port: 465, security: "ssl", username: "", from_name: "", from_email: "", enabled: true });
  const [password, setPassword] = useState("");
  const [passwordSet, setPasswordSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(endpoint);
      const d = r.data || {};
      setF({ host: d.host || "", port: d.port || 465, security: d.security || "ssl", username: d.username || "", from_name: d.from_name || "", from_email: d.from_email || "", enabled: d.enabled !== false });
      setPasswordSet(!!d.password_set);
      setTestTo(d.from_email || d.username || "");
    } catch (e) { setMsg({ type: "err", text: e.message }); }
    setLoading(false);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const payload = { ...f };
      if (password) payload.password = password;
      const r = await api.put(endpoint, payload);
      setPasswordSet(!!r.data.password_set);
      setPassword("");
      setMsg({ type: "ok", text: "SMTP-Einstellungen gespeichert." });
    } catch (e) { setMsg({ type: "err", text: e.message }); }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testTo || !testTo.includes("@")) { setMsg({ type: "err", text: "Bitte gültige Test-Empfängeradresse angeben." }); return; }
    setTesting(true); setMsg(null);
    try {
      const r = await api.post(`${endpoint}/test`, { to: testTo });
      if (r.data.ok) setMsg({ type: "ok", text: `Test-E-Mail an ${testTo} gesendet.` });
      else setMsg({ type: "err", text: `Versand fehlgeschlagen: ${r.data.error}` });
    } catch (e) { setMsg({ type: "err", text: e.message }); }
    setTesting(false);
  };

  const Field = ({ label, value, onChange, testId, type = "text", placeholder }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="h-11 rounded-none" data-testid={testId} />
    </div>
  );

  return (
    <div className="bg-white border border-black/10 p-6 max-w-3xl" data-testid="smtp-settings-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-lg font-bold flex items-center gap-2">
          <EnvelopeSimple size={18} weight="bold" style={{ color: "var(--tonneva-primary)" }} /> {title}
        </h3>
        <label className="flex items-center gap-2 text-xs text-zinc-500">
          <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} data-testid="smtp-enabled-toggle" />
          Versand aktiv
        </label>
      </div>
      <p className="text-sm text-zinc-500 mb-5">{description || "Hinterlegen Sie Ihren eigenen Mailserver, damit Newsletter und Rechnungen von Ihrer eigenen Absenderadresse versendet werden. Ohne Konfiguration wird der zentrale Tonneva-Versand genutzt."}</p>
      {loading ? <p className="text-sm">Lädt…</p> : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="SMTP-Host" value={f.host} onChange={(v) => setF({ ...f, host: v })} testId="smtp-host" placeholder="z. B. send.one.com" />
            <Field label="Port" value={f.port} onChange={(v) => setF({ ...f, port: v })} testId="smtp-port" placeholder="z. B. 587 oder 2525" />
            <div>
              <Label className="text-xs">Verschlüsselung</Label>
              <Select value={f.security} onValueChange={(v) => setF({ ...f, security: v })}>
                <SelectTrigger className="h-11 rounded-none" data-testid="smtp-security"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ssl">SSL/TLS (meist Port 465)</SelectItem>
                  <SelectItem value="starttls">STARTTLS (Port 587 oder 2525)</SelectItem>
                  <SelectItem value="none">Keine (unverschlüsselt)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-zinc-500 -mt-2">Hinweis: Port 25 funktioniert aus der Cloud in der Regel nicht – bitte <strong>STARTTLS</strong> mit Port <strong>587</strong> oder <strong>2525</strong> verwenden.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Benutzername" value={f.username} onChange={(v) => setF({ ...f, username: v })} testId="smtp-username" placeholder="info@ihre-domain.de" />
            <div>
              <Label className="text-xs">Passwort {passwordSet && <span className="text-zinc-400">(gesetzt – leer lassen zum Beibehalten)</span>}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={passwordSet ? "••••••••" : ""} className="h-11 rounded-none" data-testid="smtp-password" />
            </div>
            <Field label="Absender-Name" value={f.from_name} onChange={(v) => setF({ ...f, from_name: v })} testId="smtp-from-name" placeholder="z. B. GTR Tonnenreinigung" />
            <Field label="Absender-E-Mail" value={f.from_email} onChange={(v) => setF({ ...f, from_email: v })} testId="smtp-from-email" placeholder="info@ihre-domain.de" />
          </div>
          {msg && <div className={`mt-4 text-sm ${msg.type === "ok" ? "text-green-700" : "text-red-600"}`} data-testid="smtp-msg">{msg.text}</div>}
          <div className="flex flex-wrap items-end gap-3 mt-5 pt-5 border-t border-black/10">
            <Button onClick={save} disabled={saving} className="rounded-none" data-testid="smtp-save-btn">{saving ? "Speichert…" : "Speichern"}</Button>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-xs">Test an</Label>
                <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} className="h-11 rounded-none w-60" data-testid="smtp-test-to" />
              </div>
              <Button variant="outline" onClick={sendTest} disabled={testing} className="rounded-none" data-testid="smtp-test-btn">{testing ? "Sendet…" : "Test-E-Mail senden"}</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
